import * as _ from 'lodash';
import * as toastr from 'toastr';

import * as UserManagement from '../../';
import * as Common from '../../../../Common/modules';
import * as Models from '../../../../Models';
import * as Services from '../../../../Services';

export interface IEditUserScope extends ng.IScope {
  editUserCtrl: EditUserCtrl;
}

export class EditUserCtrl {
  private showLogging: boolean = false;
  private roleList: Models.Auth.RoleList.RoleList = new Models.Auth.RoleList.RoleList();
  public user: Models.UserModel.IUserModel;
  public suspendWorkIssueNotificationSlider =
    new UserManagement.models.suspendWorkIssueNotificationSlider.SuspendWorkIssueNotificationSlider();
  public allGroups: Models.GroupModel.IGroupModel[] = new Array<Models.GroupModel.IGroupModel>();
  public isSaving = false;
  public saveUnsuccessful = false;
  public alerts = <UserManagement.models.Alert.IAlert[]>[];
  public possiblePartners: Models.PartnerModel.IPartnerModel[] = new Array<Models.PartnerModel.IPartnerModel>();

  public static $inject = [
    "$scope",
    "$stateParams",
    "$timeout",
    "UserRetrievalService",
    "UserSaveService",
    "GroupRetrievalService",
    "PartnerRetrievalService",
    "igs.messageBox",
    "UserService",
    "$state",
  ];

  constructor(
    private $scope: IEditUserScope,
    private $stateParams: any,
    private $timeout: ng.ITimeoutService,
    private userRetrievalService: UserManagement.services.UserRetrievalService.IUserRetrievalService,
    private userSaveService: UserManagement.services.UserSaveService.IUserSaveService,
    private groupRetrievalService: UserManagement.services.GroupRetrievalService.IGroupRetrievalService,
    private partnerRetrievalService: UserManagement.services.PartnerRetrievalService.IPartnerRetrievalService,
    private messageBox: any,
    private userService: Services.UserService.IUserService,
    private $state: angular.ui.IStateService,
  ) {
    $scope.editUserCtrl = this;
    // only user managers can edit users
    this.userService.isUserInRole(this.roleList.manageUsers).then((response) => {
      if (response === false) {
        this.$state.go("main.customersearchv2");
      }
    });

    this.getAndSetUser($stateParams.id)
      .then(() => this.getAndSetGroups())
      .then(() => this.getAndSetPartners());
  }

  public saveUser = () => {
    if (this.showLogging) {
      console.log("saving user...");
    }
    this.isSaving = true;
    var allGroupsIds = this.allGroups.map(({ groupId }) => groupId);
    this.user.groups = _.filter(this.user.groups, (g: Models.GroupModel.IGroupModel) =>
      allGroupsIds.includes(g.groupId),
    );
    this.userSaveService
      .saveUser(this.user)
      .then((result: any) => {
        if (result.wasSuccessful) {
          toastr.success("Success", "User saved successfully");
        } else {
          if (result.message === "DupeEmail") {
            toastr.error("Error", "This email already exists on a different User.");
          } else {
            toastr.error("Error", "Something went wrong when attempting to save the user.");
          }
        }
      })
      .finally(() => {
        this.isSaving = false;
      });
  };

  public selectOrUnselectGroup = (group: Models.GroupModel.IGroupModel) => {
    if (this.showLogging) {
      console.log("group button clicked...");
    }
    if (this.groupIsSelected(group)) {
      this.user.groups = _.filter(this.user.groups, (g: Models.GroupModel.IGroupModel) => g.groupId !== group.groupId);
    } else {
      this.user.groups.push(group);
    }
  };

  public getButtonClass = (group: Models.GroupModel.IGroupModel) => {
    return this.groupIsSelected(group) ? "cyan-btn" : "";
  };

  public enableUser = () => {
    if (this.showLogging) {
      console.log("enabling user...");
    }
    var openBox = this.messageBox.open("Enable User", "Are you sure you want to enable this user?", [
      { label: "OK", cssClass: "btn-success", result: true },
      { label: "Cancel", cssClass: "btn-cancel", result: false },
    ]);

    openBox.then((result: boolean) => {
      if (result) {
        this.userSaveService.enableUser(this.user.userId).then(
          () => {
            this.user.isApproved = true;
            toastr.success("User enabled", "Success");
          },
          () => toastr.error("Enabling User Failed", "Error"),
        );
      }
    });
  };

  public disableUser = () => {
    if (this.showLogging) {
      console.log("disabling user...");
    }
    var openBox = this.messageBox.open("Disable User", "Are you sure you want to disable this user?", [
      { label: "OK", cssClass: "btn-success", result: true },
      { label: "Cancel", cssClass: "btn-cancel", result: false },
    ]);

    openBox.then((result: boolean) => {
      if (result) {
        this.userSaveService.disableUser(this.user.userId).then(
          () => {
            toastr.success("User disabled", "Success");
            this.user.isApproved = false;
          },
          () => {
            toastr.error("Disabling User Failed", "Error");
          },
        );
      }
    });
  };

  public unlockUser = () => {
    if (this.showLogging) {
      console.log("unlocking user...");
    }
    var openBox = this.messageBox.open("Unlock User", "Are you sure you want to unlock this user?", [
      { label: "OK", cssClass: "btn-success", result: true },
      { label: "Cancel", cssClass: "btn-cancel", result: false },
    ]);

    openBox.then((result: boolean) => {
      if (result) {
        this.userSaveService.unlockUser(this.user.userId).then(
          () => {
            toastr.success("User unlocked", "Success");
            this.user.isLocked = false;
          },
          () => {
            toastr.error("Unlocking User Failed", "Error");
          },
        );
      }
    });
  };

  public groupIsSelected = (group: Models.GroupModel.IGroupModel) => {
    return this.user && _.some(this.user.groups, (g: Models.GroupModel.IGroupModel) => g.groupId === group.groupId);
  };

  // loads the selected users data
  private getAndSetUser(id: string): ng.IPromise<void> {
    if (this.showLogging) {
      console.log(`getting user ${id}...`);
    }
    return this.userRetrievalService.getUser(id).then((user: Models.UserModel.IUserModel) => {
      this.user = user;
      this.suspendWorkIssueNotificationSlider =
        new UserManagement.models.suspendWorkIssueNotificationSlider.SuspendWorkIssueNotificationSlider();
      this.suspendWorkIssueNotificationSlider.enabled = !this.user.suspendWorkIssueNotification;
      if (this.suspendWorkIssueNotificationSlider.enabled) {
        this.suspendWorkIssueNotificationSlider.text = "Notifications - Enabled";
      } else {
        this.suspendWorkIssueNotificationSlider.text = "Notifications - Disabled";
      }
    });
  }

  public toggleSuspendWorkIssueNotification = (): void => {
    if (this.suspendWorkIssueNotificationSlider.enabled) {
      this.suspendWorkIssueNotificationSlider.enabled = false;
      this.user.suspendWorkIssueNotification = true;
    } else {
      this.suspendWorkIssueNotificationSlider.enabled = true;
      this.user.suspendWorkIssueNotification = false;
    }
  };

  // gets the possible partners for the current user
  private getAndSetPartners(): ng.IPromise<void> {
    if (this.showLogging) {
      console.log("getting partners...");
    }
    return this.partnerRetrievalService.getPartners(true).then((partners: Models.PartnerModel.IPartnerModel[]) => {
      this.possiblePartners = _.sortBy(partners, "name");
    });
  }

  // gets all groups for the users selected partner
  private getAndSetGroups(): ng.IPromise<void> {
    let partnerCode = (this.user.partner && this.user.partner.code) || "";
    if (this.showLogging) {
      console.log(`getting groups for partner ${partnerCode}...`);
    }
    if (partnerCode !== "") {
      return this.groupRetrievalService
        .getGroupsInPartner(partnerCode)
        .then((groups: Models.GroupModel.IGroupModel[]) => {
          this.allGroups = _.sortBy(groups, "name");
        });
    }
    return null;
  }
}
